import styled from 'styled-components';

import { TableProps } from './table.types';

export const Table = styled('table')<
	Pick<TableProps<unknown>, 'width' | 'minWidth' | 'borderRight' | 'borderLeft' | 'borderBottom' | 'height'>
>`
	border-collapse: collapse;
	width: ${({ width }) => width};
	min-width: ${({ minWidth }) => minWidth};
	height: ${({ height }) => height};
	font-size: ${({ theme: { table } }) => table.fontSize};
	background-color: ${({ theme: { table } }) => table.background};
	border-right: ${({ borderRight, theme: { table } }) => (borderRight ? table.border : 'none')};
	border-left: ${({ borderLeft, theme: { table } }) => (borderLeft ? table.border : 'none')};
	border-bottom: ${({ borderBottom, theme: { table } }) => (borderBottom ? table.border : 'none')};
`;

export const TableHead = styled.thead<{ isHeaderSticky?: boolean; zIndex?: number }>`
	position: relative;
	box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
	z-index: 1;

	${({ isHeaderSticky, zIndex }) =>
		isHeaderSticky &&
		`
			position: sticky;
			top: 0;
			z-index: ${zIndex || 2};
			box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.15);
  	`};
`;

export const TableBody = styled('tbody')``;

export const TableHeader = styled('th')<
	Pick<
		TableProps<unknown>,
		| 'cellPadding'
		| 'borderTop'
		| 'borderBottom'
		| 'borderRight'
		| 'borderLeft'
		| 'align'
		| 'background'
		| 'width'
		| 'zIndex'
		| 'whiteSpace'
		| 'headColor'
		| 'isHeaderSticky'
		| 'isHeaderBorderTop'
	>
>`
	font-weight: ${({ theme: { table } }) => table.head};
	padding: ${({ cellPadding }) => cellPadding};
	border-top: ${({ borderTop, theme: { table } }) => (borderTop ? table.border : 'none')};
	border-bottom: ${({ borderBottom, theme: { table } }) => (borderBottom ? table.border : 'none')};
	border-left: ${({ borderLeft, theme: { table } }) => (borderLeft ? table.border : 'none')};
	border-right: ${({ borderRight, theme: { table } }) => (borderRight ? table.border : 'none')};
	color: ${({ headColor, theme: { table } }) => headColor || table.headColor};
	background-color: ${({ background, theme: { table } }) => background || table.headBackground};
	text-align: ${({ align }) => align};
	width: ${({ width }) => width};
	z-index: ${({ zIndex }) => zIndex};
	white-space: ${({ whiteSpace }) => whiteSpace};

	${({ isHeaderSticky, isHeaderBorderTop, theme: { box } }) =>
		isHeaderSticky && isHeaderBorderTop && `box-shadow: inset 0 1px 0 ${box.border}`};
  	};

	& > div > svg {
		cursor: pointer;
	}

	& > div > span {
		white-space: nowrap;
	}

	@media ${({ theme: { media } }) => media.mobile} {
		padding: 8px 22px;
	}
`;

export const TableRow = styled('tr')<
	Pick<TableProps<unknown>, 'borderTop' | 'borderBottom' | 'borderRight' | 'borderLeft' | 'striped'> & {
		showDashedBorder?: boolean;
		errorRow?: boolean;
	}
>`
	font-weight: ${({ theme: { table } }) => table.body};
	border-left: ${({ borderLeft, theme: { table } }) => (borderLeft ? table.border : 'none')};
	border-right: ${({ borderRight, theme: { table } }) => (borderRight ? table.border : 'none')};
	position: relative;
	background-color: ${({ errorRow, theme: { isDark, table } }) =>
		errorRow ? `rgba(255, 87, 97, ${isDark ? 0.1 : 0.05}) !important` : table.background};

	${({ showDashedBorder, errorRow, theme: { box, colors } }) =>
		showDashedBorder &&
		`&::after {
			content: '';
			position: absolute;
			top: 4px;
			bottom: 4px;
			left: 8px;
			right: 56px;
			border: 1px dashed ${errorRow ? colors.error : box.border};
			border-radius: 2px;
			pointer-events: none;
		}`}

	&:nth-child(even) {
		background-color: ${({ striped, theme: { table } }) => striped && table.measurementBackground};
	}
`;

export const TableCell = styled('td')<
	Pick<
		TableProps<unknown>,
		| 'cellPadding'
		| 'borderBottom'
		| 'borderLeft'
		| 'borderRight'
		| 'align'
		| 'whiteSpace'
		| 'isHighlighted'
		| 'cursor'
		| 'background'
		| 'borderTop'
	>
>`
	border-bottom: ${({ borderBottom, theme: { table } }) => (borderBottom ? table.border : 'none')};
	border-right: ${({ borderRight, theme: { table } }) => (borderRight ? table.border : 'none')};
	border-left: ${({ borderLeft, theme: { table } }) => (borderLeft ? table.border : 'none')};
	border-top: ${({ borderTop, theme: { table } }) => (borderTop ? table.border : 'none')};
	color: ${({ isHighlighted, theme: { colors, table } }) => (isHighlighted ? colors.success : table.bodyColor)};
	padding: ${({ cellPadding }) => cellPadding};
	text-align: ${({ align }) => align};
	white-space: ${({ whiteSpace }) => whiteSpace};
	cursor: ${({ cursor }) => cursor};
	background: ${({ background }) => background};

	@media ${({ theme: { media } }) => media.mobile} {
		padding: 8px 22px;
	}
`;
